import { useState, useEffect } from 'react';
import { FileText, Image, CheckCircle, XCircle, Building2, Briefcase } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { updateCopyVariant, updateVisual, createFeedbackItem, createCopyFeedbackItem, getFeedbackItemsByVisual, getFeedbackItemsByCopyVariant } from '../../lib/api/content';
import type { DbCopyVariant, DbVisual, DbFeedbackItem } from '../../lib/api/content';
import { UpdateNotifications } from '../components/UpdateNotifications';

interface VisualWithFeedback extends DbVisual {
  feedbackItems?: DbFeedbackItem[];
}

interface CopyVariantWithFeedback extends DbCopyVariant {
  feedbackItems?: DbFeedbackItem[];
}

interface VacancyData {
  id: string;
  title: string;
  vacancy_text: string;
  created_at: string;
  review_completed_at: string | null;
  client_id: string;
  client: {
    name: string;
  };
  copyVariants: CopyVariantWithFeedback[];
  visuals: VisualWithFeedback[];
}

interface SharedVacancyProps {
  shareToken: string;
}

export function SharedVacancy({ shareToken }: SharedVacancyProps) {
  const [vacancy, setVacancy] = useState<VacancyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  useEffect(() => {
    loadVacancy();
  }, [shareToken]);

  const loadVacancy = async () => {
    try {
      const { data: vacancyData, error: vacancyError } = await supabase
        .from('vacancies')
        .select(`
          id,
          title,
          vacancy_text,
          created_at,
          review_completed_at,
          client_id,
          clients!inner(name)
        `)
        .eq('share_token', shareToken)
        .maybeSingle();

      if (vacancyError || !vacancyData) {
        setError('Deze link is ongeldig of verlopen.');
        setLoading(false);
        return;
      }

      const [copyVariantsResult, visualsResult] = await Promise.all([
        supabase
          .from('copy_variants')
          .select('id, vacancy_id, title, body, selected, status, feedback')
          .eq('vacancy_id', vacancyData.id)
          .eq('selected', true)
          .order('created_at', { ascending: false }),
        supabase
          .from('visuals')
          .select('id, vacancy_id, title, url, selected, status, feedback')
          .eq('vacancy_id', vacancyData.id)
          .eq('selected', true)
          .order('created_at', { ascending: false })
      ]);

      const copyVariants = copyVariantsResult.data || [];
      const visuals = visualsResult.data || [];

      const [copyVariantsWithFeedback, visualsWithFeedback] = await Promise.all([
        Promise.all(
          copyVariants.map(async (copyVariant) => {
            const feedbackItems = await getFeedbackItemsByCopyVariant(copyVariant.id);
            return {
              ...copyVariant,
              feedbackItems
            };
          })
        ),
        Promise.all(
          visuals.map(async (visual) => {
            const feedbackItems = await getFeedbackItemsByVisual(visual.id);
            return {
              ...visual,
              feedbackItems
            };
          })
        )
      ]);

      setVacancy({
        id: vacancyData.id,
        title: vacancyData.title,
        vacancy_text: vacancyData.vacancy_text,
        created_at: vacancyData.created_at,
        review_completed_at: vacancyData.review_completed_at,
        client_id: vacancyData.client_id,
        client: {
          name: (vacancyData.clients as any).name
        },
        copyVariants: copyVariantsWithFeedback,
        visuals: visualsWithFeedback
      });

      setLoading(false);
    } catch (err) {
      console.error('Error loading vacancy:', err);
      setError('Er is een fout opgetreden bij het laden van de vacature.');
      setLoading(false);
    }
  };

  const checkIfAllReviewed = (updatedVacancy: VacancyData) => {
    const allCopyReviewed = updatedVacancy.copyVariants.every(cv =>
      cv.status === 'approved' || cv.status === 'rejected'
    );
    const allVisualsReviewed = updatedVacancy.visuals.every(v =>
      v.status === 'APPROVED' || v.status === 'REJECTED'
    );

    if (allCopyReviewed && allVisualsReviewed && !updatedVacancy.review_completed_at) {
      setShowCompletionModal(true);
    }
  };

  const handleApproveCopy = async (id: string) => {
    const updated = await updateCopyVariant(id, { status: 'approved' });
    if (updated && vacancy) {
      const updatedVacancy = {
        ...vacancy,
        copyVariants: vacancy.copyVariants.map(cv => cv.id === id ? updated : cv)
      };
      setVacancy(updatedVacancy);
      checkIfAllReviewed(updatedVacancy);
    }
  };

  const handleRejectCopy = async (id: string, feedback: string) => {
    const updated = await updateCopyVariant(id, { status: 'rejected', feedback });

    if (feedback && feedback.trim()) {
      await createCopyFeedbackItem(id, feedback.trim());
    }

    if (updated && vacancy) {
      const feedbackItems = await getFeedbackItemsByCopyVariant(id);
      const updatedCopyWithFeedback = {
        ...updated,
        feedbackItems
      };

      const updatedVacancy = {
        ...vacancy,
        copyVariants: vacancy.copyVariants.map(cv => cv.id === id ? updatedCopyWithFeedback : cv)
      };
      setVacancy(updatedVacancy);
      checkIfAllReviewed(updatedVacancy);

      const { data: clientData } = await supabase
        .from('clients')
        .select('consultant_id, profiles!inner(name, email)')
        .eq('id', vacancy.client_id)
        .maybeSingle();

      if (clientData) {
        const notificationPayload = {
          notification_type: 'feedback_received',
          title: 'Feedback ontvangen op tekst',
          message: `${vacancy.client.name} heeft feedback gegeven op "${updated.title}"`,
          vacancy_title: vacancy.title,
          client_name: vacancy.client.name,
          consultant_name: (clientData.profiles as any).name,
          consultant_email: (clientData.profiles as any).email,
          copy_variant_title: updated.title,
          feedback: feedback,
          status: 'rejected'
        };

        await supabase.functions.invoke('send-notification-webhook', {
          body: notificationPayload
        });
      }
    }
  };

  const handleApproveVisual = async (id: string) => {
    const updated = await updateVisual(id, { status: 'APPROVED' });
    if (updated && vacancy) {
      const updatedVacancy = {
        ...vacancy,
        visuals: vacancy.visuals.map(v => v.id === id ? updated : v)
      };
      setVacancy(updatedVacancy);
      checkIfAllReviewed(updatedVacancy);
    }
  };

  const handleRejectVisual = async (id: string, feedback: string) => {
    const updated = await updateVisual(id, { status: 'REJECTED', feedback });

    if (feedback && feedback.trim()) {
      await createFeedbackItem(id, feedback.trim());
    }

    if (updated && vacancy) {
      const updatedVacancy = {
        ...vacancy,
        visuals: vacancy.visuals.map(v => v.id === id ? updated : v)
      };
      setVacancy(updatedVacancy);
      checkIfAllReviewed(updatedVacancy);

      const { data: clientData } = await supabase
        .from('clients')
        .select('consultant_id, profiles!inner(name, email)')
        .eq('id', vacancy.client_id)
        .maybeSingle();

      if (clientData) {
        const notificationPayload = {
          notification_type: 'feedback_received',
          title: 'Feedback ontvangen op visual',
          message: `${vacancy.client.name} heeft feedback gegeven op "${updated.title}"`,
          vacancy_title: vacancy.title,
          client_name: vacancy.client.name,
          consultant_name: (clientData.profiles as any).name,
          consultant_email: (clientData.profiles as any).email,
          visual_title: updated.title,
          feedback: feedback,
          status: 'REJECTED'
        };

        await supabase.functions.invoke('send-notification-webhook', {
          body: notificationPayload
        });
      }
    }
  };

  const handleCompleteReview = async () => {
    if (!vacancy) return;

    try {
      const { error } = await supabase
        .from('vacancies')
        .update({ review_completed_at: new Date().toISOString() })
        .eq('id', vacancy.id);

      if (error) throw error;

      setVacancy({
        ...vacancy,
        review_completed_at: new Date().toISOString()
      });
      setShowCompletionModal(false);

      const { data: clientData } = await supabase
        .from('clients')
        .select('consultant_id, profiles!inner(name, email)')
        .eq('id', vacancy.client_id)
        .maybeSingle();

      if (clientData) {
        const approvedCopyCount = vacancy.copyVariants.filter(cv => cv.status === 'approved').length;
        const rejectedCopyCount = vacancy.copyVariants.filter(cv => cv.status === 'rejected').length;
        const approvedVisualCount = vacancy.visuals.filter(v => v.status === 'APPROVED').length;
        const rejectedVisualCount = vacancy.visuals.filter(v => v.status === 'REJECTED').length;

        const notificationPayload = {
          notification_type: 'review_completed',
          title: 'Review voltooid door klant',
          message: `${vacancy.client.name} heeft de review van "${vacancy.title}" voltooid: ${approvedCopyCount + approvedVisualCount} goedgekeurd, ${rejectedCopyCount + rejectedVisualCount} afgekeurd`,
          vacancy_title: vacancy.title,
          client_name: vacancy.client.name,
          consultant_name: (clientData.profiles as any).name,
          consultant_email: (clientData.profiles as any).email,
          approved_count: approvedCopyCount + approvedVisualCount,
          rejected_count: rejectedCopyCount + rejectedVisualCount,
          feedback_count: vacancy.copyVariants.filter(cv => cv.status === 'rejected' && cv.feedback).length +
                         vacancy.visuals.filter(v => v.status === 'REJECTED' && v.feedback).length,
          completed_at: new Date().toISOString()
        };

        await supabase.functions.invoke('send-notification-webhook', {
          body: notificationPayload
        });
      }
    } catch (err) {
      console.error('Error completing review:', err);
      alert('Er is iets misgegaan. Probeer het opnieuw.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !vacancy) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Link niet gevonden</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const pendingCopyCount = vacancy.copyVariants.filter(cv => cv.status === 'pending' || !cv.status).length;
  const pendingVisualCount = vacancy.visuals.filter(v => v.status === 'UPLOADED').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start justify-between mb-3">
            <img
              src="/NEEDLES Logo (Rechthoekig).svg"
              alt="Needles Logo"
              className="h-8 sm:h-10"
            />
            <UpdateNotifications vacancyId={vacancy.id} />
          </div>
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-700">{vacancy.client.name}</h2>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{vacancy.title}</h1>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-600">
            <span>{pendingCopyCount} tekst{pendingCopyCount !== 1 ? 'en' : ''} te reviewen</span>
            <span>•</span>
            <span>{pendingVisualCount} visual{pendingVisualCount !== 1 ? 's' : ''} te reviewen</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <FileText className="w-5 h-5 text-gray-700" />
              <h2 className="text-xl font-semibold text-gray-900">Tekstvarianten</h2>
              <span className="text-sm text-gray-500">({vacancy.copyVariants.length})</span>
            </div>

            <div className="space-y-4">
              {vacancy.copyVariants.map((variant) => (
                <CopyVariantCard
                  key={variant.id}
                  variant={variant}
                  onApprove={() => handleApproveCopy(variant.id)}
                  onReject={(feedback) => handleRejectCopy(variant.id, feedback)}
                />
              ))}

              {vacancy.copyVariants.length === 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">Nog geen tekstvarianten gedeeld</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-6">
              <Image className="w-5 h-5 text-gray-700" />
              <h2 className="text-xl font-semibold text-gray-900">Visuals</h2>
              <span className="text-sm text-gray-500">({vacancy.visuals.length})</span>
            </div>

            <div className="space-y-4">
              {vacancy.visuals.map((visual) => (
                <VisualCard
                  key={visual.id}
                  visual={visual}
                  onApprove={() => handleApproveVisual(visual.id)}
                  onReject={(feedback) => handleRejectVisual(visual.id, feedback)}
                />
              ))}

              {vacancy.visuals.length === 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                  <Image className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">Nog geen visuals gedeeld</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showCompletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Alle content beoordeeld!
              </h2>
              <p className="text-gray-600">
                U heeft alle tekstvarianten en visuals beoordeeld. Klik op "Verstuur beoordeling" om uw feedback door te sturen naar de consultant.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCompletionModal(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleCompleteReview}
                className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Verstuur beoordeling
              </button>
            </div>
          </div>
        </div>
      )}

      {vacancy.review_completed_at && (
        <div className="fixed bottom-6 right-6 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">Beoordeling verstuurd!</span>
        </div>
      )}
    </div>
  );
}

function CopyVariantCard({
  variant,
  onApprove,
  onReject
}: {
  variant: CopyVariantWithFeedback;
  onApprove: () => void;
  onReject: (feedback: string) => void;
}) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');

  const feedbackItems = variant.feedbackItems || [];
  const resolvedFeedbackItems = feedbackItems.filter(f => f.status === 'resolved');
  const hasResolvedFeedback = resolvedFeedbackItems.length > 0;

  const getStatusColor = () => {
    switch (variant.status) {
      case 'approved':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'rejected':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  const getStatusLabel = () => {
    switch (variant.status) {
      case 'approved':
        return 'Goedgekeurd';
      case 'rejected':
        return 'Afgekeurd';
      default:
        return 'Te reviewen';
    }
  };

  const handleReject = () => {
    onReject(feedback);
    setShowFeedback(false);
    setFeedback('');
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{variant.title}</h3>
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${getStatusColor()}`}>
          {getStatusLabel()}
        </span>
      </div>

      <p className="text-gray-700 leading-relaxed mb-4 whitespace-pre-wrap text-sm sm:text-base">{variant.body}</p>

      {hasResolvedFeedback && variant.status === 'pending' && (
        <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 mb-4">
          <p className="text-sm font-bold text-blue-900 mb-3">
            Uw feedback is verwerkt - Beoordeel deze tekst opnieuw
          </p>
          <div className="space-y-2">
            {resolvedFeedbackItems.map((item) => (
              <div key={item.id} className="flex items-start gap-2 bg-white rounded-lg p-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-700">{item.feedback_text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {variant.status === 'rejected' && variant.feedback && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-800"><strong>Uw feedback:</strong> {variant.feedback}</p>
        </div>
      )}

      {variant.status === 'pending' && (
        <div className="space-y-3">
          {!showFeedback && (
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={onApprove}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Goedkeuren
              </button>
              <button
                onClick={() => setShowFeedback(true)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Feedback
              </button>
            </div>
          )}

          {showFeedback && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Feedback (optioneel)
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Wat kan er beter aan deze tekst?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
                  rows={3}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleReject}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Verstuur feedback
                </button>
                <button
                  onClick={() => {
                    setShowFeedback(false);
                    setFeedback('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Annuleren
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VisualCard({
  visual,
  onApprove,
  onReject
}: {
  visual: VisualWithFeedback;
  onApprove: () => void;
  onReject: (feedback: string) => void;
}) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');

  const feedbackItems = visual.feedbackItems || [];
  const resolvedFeedbackItems = feedbackItems.filter(f => f.status === 'resolved');
  const hasResolvedFeedback = resolvedFeedbackItems.length > 0;

  const getStatusColor = () => {
    switch (visual.status) {
      case 'APPROVED':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'REJECTED':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  const getStatusLabel = () => {
    switch (visual.status) {
      case 'APPROVED':
        return 'Goedgekeurd';
      case 'REJECTED':
        return 'Afgekeurd';
      default:
        return 'Te reviewen';
    }
  };

  const handleReject = () => {
    onReject(feedback);
    setShowFeedback(false);
    setFeedback('');
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{visual.title}</h3>
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${getStatusColor()}`}>
          {getStatusLabel()}
        </span>
      </div>

      <div className="mb-4 rounded-lg overflow-hidden bg-gray-100">
        <img
          src={visual.url}
          alt={visual.title}
          className="w-full h-auto"
        />
      </div>

      {hasResolvedFeedback && visual.status === 'UPLOADED' && (
        <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 mb-4">
          <p className="text-sm font-bold text-blue-900 mb-3">
            Uw feedback is verwerkt - Beoordeel deze visual opnieuw
          </p>
          <div className="space-y-2">
            {resolvedFeedbackItems.map((item) => (
              <div key={item.id} className="flex items-start gap-2 bg-white rounded-lg p-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-700">{item.feedback_text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {visual.status === 'REJECTED' && visual.feedback && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-800"><strong>Uw feedback:</strong> {visual.feedback}</p>
        </div>
      )}

      {visual.status === 'UPLOADED' && (
        <div className="space-y-3">
          {!showFeedback && (
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={onApprove}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Goedkeuren
              </button>
              <button
                onClick={() => setShowFeedback(true)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Feedback
              </button>
            </div>
          )}

          {showFeedback && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Feedback (optioneel)
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Wat kan er beter aan deze visual?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
                  rows={3}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleReject}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Verstuur feedback
                </button>
                <button
                  onClick={() => {
                    setShowFeedback(false);
                    setFeedback('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Annuleren
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
